import { refreshTiming } from './webSession';

/**
 * EV-462。更新（refresh）を送ってから、その結果を生きた画面が受け取るまでの印（localStorage。タブをまたいで見える）。
 *
 * サーバーは更新のたびに refresh を回転させ、古い refresh をすぐに失効させる（BLACKLIST_AFTER_ROTATION）。
 * 画面の読み込み（再読み込み・ログインの直後の移動）の更新の途中で別の経路を開くと、前の画面の要求は
 * 切られ、サーバーが回転させた新しい Cookie をブラウザが受け取れない。次の画面の更新は失効した古い Cookie を
 * 送って 401 になり、ログインの画面へ戻っていた。
 *  - 更新は keepalive で送る（画面を離れても要求を最後まで送り、応答の新しい Cookie をブラウザが保存する）。
 *  - 応答の前に画面を離れると、この印が残る。どのタブの更新でも、印があるときに 401 token_not_valid（失効した
 *    refresh）なら、前の画面の更新がまだ終わっていないだけかもしれないので、少し待って取り直す。
 *  - #394 の Codex の指摘 4369638061。離れた画面の keepalive の応答は、錠の外で Cookie を書く。その応答が新しい
 *    ログインの後に届いても、新しいログインの Cookie を上書きしないこと。以前はログインが印の消えるのを決まった
 *    秒数（10秒）まで待っていたが、応答はそれより遅れることがあり（REVIEW-1027-01）、待つ間ログインも遅れた。
 *    EV-491 で Cookie の Path をログインごとにした（webSessionPath）。前のセッションの応答は前のセッションの
 *    Cookie しか書けないので、ログイン・2段階認証の最後の段・サポートの入る・戻るは待たない。
 * 印は、生きた画面が更新の応答（成功・失敗）を受け取ったら消す。失効した refresh の 401 で取り直すときは残す
 * （前の画面の更新の応答がまだ届いていない）。
 */
export const REFRESH_PENDING_KEY = 'yometel-admin-refresh-pending';
/** 取り直しの判断で印を信じる長さ。これより古い印は、別の理由で残ったものとして扱う。 */
const REFRESH_PENDING_MAX_AGE_MS = 60000;

export function markRefreshPending(): void {
  try {
    localStorage.setItem(REFRESH_PENDING_KEY, String(Date.now()));
  } catch {
    /* 保存領域が使えない環境では、取り直さない・待たない（今までと同じ） */
  }
}

export function clearRefreshPending(): void {
  try {
    localStorage.removeItem(REFRESH_PENDING_KEY);
  } catch {
    /* 同上 */
  }
}

/** 印の古さ（ミリ秒）。印が無い・読めないなら null。 */
function refreshPendingAge(): number | null {
  try {
    const value = localStorage.getItem(REFRESH_PENDING_KEY);
    if (value === null) return null;
    const age = Date.now() - Number(value);
    return Number.isFinite(age) && age >= 0 ? age : null;
  } catch {
    return null;
  }
}

/** 前の画面が、更新の応答を受け取る前に離れていたか（新しい印）。 */
export function wasRefreshInterrupted(): boolean {
  const age = refreshPendingAge();
  return age !== null && age < REFRESH_PENDING_MAX_AGE_MS;
}

/** 失効した refresh を送ったときの 401（回転した後の古い Cookie）。 */
export function isStaleRefresh(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: { code?: unknown } } })?.response;
  return response?.status === 401 && response.data?.code === 'token_not_valid';
}

export const pause = (ms: number) =>
  ms > 0 ? new Promise((resolve) => setTimeout(resolve, ms)) : Promise.resolve();

/**
 * #425 の Codex の指摘 4392002894。Cookie を資格にする要求（サポートの入る・戻る）を送る（錠の中で呼ぶ）。
 * 前の画面の同じセッションの更新が途中（印がある）なら、その更新がサーバーで回転させた後の古い Cookie を送って
 * 401 token_not_valid になり得る。更新（postRefresh）と同じく、少し待って取り直す（回転した Cookie が届くのを待つ）。
 * 印が無い・別の失敗なら、そのまま返す。印は消さない（前の画面の更新の応答を受け取ったわけではない）。
 */
export async function postRecoveringInterruptedRefresh<T>(send: () => Promise<T>): Promise<T> {
  const interrupted = wasRefreshInterrupted();
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await send();
    } catch (error) {
      if (!(interrupted && attempt < refreshTiming.interruptedRetries && isStaleRefresh(error))) throw error;
      await pause(refreshTiming.interruptedRetryDelayMs);
    }
  }
}
